import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, GameStateConfigListItem } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export interface UseAdminGameConfigsReturn {
  /**
   * List rows, which carry no `contentHash` — the server omits it rather than
   * canonicalizing a whole page of blobs. Fetch a keyword (`useAdminGameConfig`)
   * when you need its hash.
   */
  configs: GameStateConfigListItem[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

/** List all global config entries (admin). */
export function useAdminGameConfigs(options?: {
  limit?: number;
  offset?: number;
}): UseAdminGameConfigsReturn {
  const filters = { limit: options?.limit, offset: options?.offset };
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.gameState.configsList(filters),
    queryFn: () => api.adminListGameStateConfigs(filters),
  });

  return {
    configs: data?.configs ?? [],
    total: data?.total ?? 0,
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load configs' : null,
    refresh: () => {
      refetch();
    },
  };
}

/** Fetch a single config entry by keyword (admin). */
export function useAdminGameConfig(keyword: string) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.gameState.configDetail(keyword),
    queryFn: () => api.adminGetGameStateConfig(keyword),
    enabled: !!keyword,
    // A 404 means "no config under this keyword yet" — a normal state the
    // detail page renders as create mode, not a transient failure to retry.
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 2,
  });
  const notFound = error instanceof ApiError && error.status === 404;
  return {
    config: data?.config ?? null,
    notFound,
    loading: isLoading,
    error: notFound
      ? null
      : error instanceof Error
        ? error.message
        : error
          ? 'Failed to load config'
          : null,
    refresh: () => {
      refetch();
    },
  };
}

function invalidateConfigQueries(qc: ReturnType<typeof useQueryClient>): void {
  qc.invalidateQueries({ queryKey: queryKeys.gameState.configs() });
}

interface UpsertConfigInput {
  keyword: string;
  data: unknown;
  description?: string | null;
}

export function useUpsertGameConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ keyword, data, description }: UpsertConfigInput) =>
      api.adminUpsertGameStateConfig(keyword, data, description),
    onSuccess: () => invalidateConfigQueries(qc),
  });
}

export function useDeleteGameConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (keyword: string) => api.adminDeleteGameStateConfig(keyword),
    onSuccess: () => invalidateConfigQueries(qc),
  });
}
